import React from 'react'
import gql from 'graphql-tag'
import Query from 'react-apollo/Query'
import {
  CustomMetadataField,
  CustomMetadataRestrictSettings,
  Importance
} from './index'
import { Policy } from '../policy'

export const settingsQuery = gql`
  query applicationsSettingsForm($organizationID: ID!) {
    organizationApplicationsSettings(organizationID: $organizationID) {
      id
      importance
      autoLicense
      filterAppMostRecentSession
      preventOnboardingFailingApplications
      preventOnboardingFailingApplicationsBySession
      remediationPolicies {
        id
        name
      }
      compliancePolicies {
        id
        name
      }
      routeExpirationDays
      protectRuleEnv
    }

    compliancePolicies(organizationID: $organizationID) {
      policies {
        id
        name
        enabled
      }
    }

    remediationPolicies(organizationID: $organizationID) {
      policies {
        id
        name
        enabled
        action
      }
    }

    customMetadataFields(organizationID: $organizationID) {
      fields {
        id
        type
        displayLabel
        agentLabel
        required
        unique
        subfields {
          id
          type
          displayLabel
          agentLabel
          required
          unique
        }
      }
    }

    customMetadataSettings(input: { organizationID: $organizationID }) {
      newAppAction
      existingAppAction
    }
  }
`

interface SettingsData {
  organizationApplicationsSettings: {
    importance: Importance
    autoLicense: boolean
    filterAppMostRecentSession: boolean
    preventOnboardingFailingApplications: boolean
    preventOnboardingFailingApplicationsBySession: boolean
    protectRuleEnv: string
    routeExpirationDays: number | null
    remediationPolicies: Policy[]
    compliancePolicies: Policy[]
  }
  compliancePolicies: {
    policies: Policy[]
  }
  remediationPolicies: {
    policies: Policy[]
  }
  customMetadataFields: {
    fields: CustomMetadataField[]
  }
  customMetadataSettings: CustomMetadataRestrictSettings
}

interface SettingsQueryArgs {
  organizationID: string
}

interface Data {
  availableCompliancePolicies: Policy[]
  availableRemediationPolicies: Policy[]
  autoLicense: boolean
  filterAppMostRecentSession: boolean
  preventOnboardingFailingApplications: boolean
  preventOnboardingFailingApplicationsBySession: boolean
  protectRuleEnv: string
  routeExpirationDays: number | null
  remediationPolicies: Policy[]
  compliancePolicies: Policy[]
  importance: Importance
  customMetadataFields: CustomMetadataField[]
  customMetadataSettings: CustomMetadataRestrictSettings
}

export function OrganizationSettingsQuery({
  organizationID,
  children
}: {
  organizationID: string
  children: (props: { settings: Data }) => React.ReactNode
}) {
  return (
    <Query<SettingsData, SettingsQueryArgs>
      query={settingsQuery}
      variables={{ organizationID }}
      fetchPolicy="network-only"
    >
      {({ data, error, loading }) => {
        if (error || loading || !data) {
          return <div />
        }

        const {
          compliancePolicies: { policies: availableCompliancePolicies },
          remediationPolicies: { policies: availableRemediationPolicies },
          organizationApplicationsSettings: {
            autoLicense,
            filterAppMostRecentSession,
            preventOnboardingFailingApplications,
            preventOnboardingFailingApplicationsBySession,
            protectRuleEnv,
            routeExpirationDays,
            remediationPolicies,
            compliancePolicies,
            importance
          },
          customMetadataFields: { fields },
          customMetadataSettings
        } = data

        const settings = {
          availableCompliancePolicies,
          availableRemediationPolicies,
          autoLicense,
          filterAppMostRecentSession,
          preventOnboardingFailingApplications,
          preventOnboardingFailingApplicationsBySession,
          protectRuleEnv,
          routeExpirationDays,
          remediationPolicies,
          compliancePolicies,
          importance,
          customMetadataFields: fields,
          customMetadataSettings
        }

        return children({
          settings
        })
      }}
    </Query>
  )
}
